import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import { FeedPerson, FeedRunCard } from "@signalist/shared-types";

import { useAppSelector } from "@/redux/hooks";
import { useSnackBar } from "@/utils/useSnackbar";

import { authSelector } from "@/pages/Public/redux/auth.slice";

import { useGetSendersUsersQuery } from "@/pages/Private/redux/admin/admin.api";
import { useGenerateCopywritingMutation } from "@/pages/Private/redux/copywritingOrchestration/copywritingOrchestration.api";
import { copywritingOrchestrationSelector } from "@/pages/Private/redux/copywritingOrchestration/copywritingOrchestration.slice";

import { CopywritingOutputFormat } from "@/pages/Private/pages/Messages/components/GenerateBar/types";
import { resolveDefaultGenerateSender } from "@/pages/Private/pages/List/components/CampaignOverview/utils/resolveDefaultGenerateSender.util";

export interface FeedCardGenerationRequest {
	card: FeedRunCard;
	/** The card person the sequence is for — must carry prospectId + listPersonId (D3). */
	person: FeedPerson;
	/** Only set for row-seeded generations — maps the job to that signal row (D4). */
	seedSignalId?: number;
	/** Seed text (+ appended Tell-AI instructions). */
	userInstruction: string;
}

/**
 * Feed-scoped AI draft generation (PREC-3635 P6 D3): fires the direct
 * copywriting call for a run-card person with the `useFeedDraftGeneration`
 * call contract (default sender resolution, combined channel, auto language,
 * full sequence, 409/no-sender snackbars) — but addressed explicitly by
 * `{ card, person }` instead of the list slice's inbox, so it works on the
 * Feed page. Live progress arrives via the page-root company-room socket
 * ({@link useCompanyGenerationsSocket}), not a per-prospect socket.
 */
export const useFeedCardGeneration = () => {
	const { t } = useTranslation();
	const { handleOpenSnackbar, renderMessageSnackbar } = useSnackBar();

	const { user } = useAppSelector(authSelector);
	const { frameworkOverrides, freshStartOverride } = useAppSelector(
		copywritingOrchestrationSelector
	);

	const { data: sendingUsers } = useGetSendersUsersQuery();
	const [generateCopywriting] = useGenerateCopywritingMutation();

	const generateForPerson = useCallback(
		async ({
			card,
			person,
			seedSignalId,
			userInstruction,
		}: FeedCardGenerationRequest): Promise<boolean> => {
			// The backend resolves the listPerson from listId + prospectId — without
			// both the call can only 404, so the CTA never renders then (D3).
			if (!person.prospectId || !person.listPersonId) {
				return false;
			}

			const sender = resolveDefaultGenerateSender(sendingUsers?.data, user?.id, {
				prospectHasEmail: !!person.email,
				prospectHasLinkedinUrl: !!person.linkedinUrl,
			});

			if (!sender) {
				handleOpenSnackbar(t("profileTracker.signalActions.generateNoSender"));

				return false;
			}

			try {
				await generateCopywriting({
					prospectId: person.prospectId,
					listId: card.listId,
					channel: sender.channel,
					language: "auto",
					senderUserId: sender.senderUserId,
					senderEmailId: sender.senderEmailId,
					userInstruction: userInstruction || undefined,
					linkedinInviteNote: sender.linkedinInviteNote,
					outputFormat: CopywritingOutputFormat.FULL_SEQUENCE,
					frameworkOverrides,
					freshStartOverride: freshStartOverride || undefined,
					seedSignalId,
				}).unwrap();

				return true;
			} catch (error) {
				const status = (error as { status?: number } | undefined)?.status;

				handleOpenSnackbar(
					status === 409
						? t("profileTracker.signalActions.generateAlreadyInProgress")
						: t("profileTracker.signalActions.generateError")
				);

				return false;
			}
		},
		[
			sendingUsers?.data,
			user?.id,
			frameworkOverrides,
			freshStartOverride,
			generateCopywriting,
			handleOpenSnackbar,
			t,
		]
	);

	return { generateForPerson, renderFeedGenerationSnackbar: renderMessageSnackbar };
};
